import i18n from "../i18n";
import { computed } from "vue";
import useCompanyPreferences from "@/composables/useCompanyPreferences";
const { t } = i18n.global;

export const getUnitsArray = () => [
  { name: t("Centimeters"), code: "cm" },
  { name: t("Meters"), code: "m" },
  { name: t("Inches"), code: "in" },
  { name: t("Feet"), code: "ft" },
  { name: t("Yards"), code: "yd" },
  { name: t("Repetitions"), code: "rep" },
];

export function useDefaultMeasurementUnit() {
  const { companyPreferences } = useCompanyPreferences();

  const defaultUnitCode = computed(() => companyPreferences.value?.defaultMeasurementUnit || "m");

  const defaultUnitName = computed(() => {
    const unit = getUnitsArray().find((u) => u.code === defaultUnitCode.value);
    return unit ? unit.name : t("Meters");
  });

  const defaultUnitCodeShort = computed(() => {
    const unit = getUnitsArray().find((u) => u.code === defaultUnitCode.value);
    return unit ? unit.code : "m";
  });

  return {
    defaultUnitCode,
    defaultUnitName,
    defaultUnitCodeShort,
  };
}

const POINTS_PER_INCH = 72;
const INCHES_PER_CM = 1 / 2.54;

export const SUPPORTED_UNITS = ["pt", "in", "m", "cm", "mm", "yd", "ft", "rep"] as const;
export type Unit = (typeof SUPPORTED_UNITS)[number];

export function convertToPoints(value: number | null, fromUnit: string): number | null {
  if (!value || !fromUnit) return null;
  switch (fromUnit.toLowerCase()) {
    case "pt":
      return value;
    case "in":
      return value * POINTS_PER_INCH;
    case "m":
      return value * POINTS_PER_INCH * INCHES_PER_CM * 100;
    case "cm":
      return value * POINTS_PER_INCH * INCHES_PER_CM;
    case "mm":
      return (value * POINTS_PER_INCH * INCHES_PER_CM) / 10;
    case "yd":
      return value * POINTS_PER_INCH * 36;
    case "ft":
      return value * POINTS_PER_INCH * 12;
    default:
      throw new Error(`Unsupported unit: ${fromUnit}`);
  }
}

export function convertFromPoints(valueInPoints: number | null, toUnit: string): number | null {
  if (!valueInPoints || !toUnit) return null;
  switch (toUnit) {
    case "pt":
      return valueInPoints;
    case "in":
      return valueInPoints / POINTS_PER_INCH;
    case "m":
      return valueInPoints / (POINTS_PER_INCH * INCHES_PER_CM * 100);
    case "cm":
      return valueInPoints / (POINTS_PER_INCH * INCHES_PER_CM);
    case "mm":
      return (valueInPoints / (POINTS_PER_INCH * INCHES_PER_CM)) * 10;
    case "yd":
      return valueInPoints / (POINTS_PER_INCH * 36);
    case "ft":
      return valueInPoints / (POINTS_PER_INCH * 12);
    default:
      throw new Error(`Unsupported unit: ${toUnit}`);
  }
}
